import { array, object, optional, string } from 'zod';

import {
  countQuizText,
  hasFormulaOverLength,
  ITEM_FORMULA_LATEX_MAX_LENGTH,
} from '@/shared/features/rich-text/utils/countQuizText';
import { countVisibleQuizText } from '@/shared/features/rich-text/utils/countVisibleQuizText';

import { hasQuizItemContent } from './quizItemContent';

export const MIN_MATCH_PAIR_COUNT = 2;
export const MAX_MATCH_PAIR_COUNT = 5;

export const MATCH_ITEM_CONTENT_REQUIRED_MESSAGE =
  'Match item text, equation or image is required';
export const MATCH_ITEM_TEXT_MAX_LENGTH = 50;
const MATCH_PAIR_SEPARATOR = ':';

const MatchItemIdSchema = string()
  .min(1, 'Match item id is required')
  .refine(id => !id.includes(MATCH_PAIR_SEPARATOR), {
    message: 'Match item id cannot contain the pair separator',
  });

const createMatchItemSchema = (countItemText: (text: string) => number) =>
  object({
    id: MatchItemIdSchema,
    text: string().refine(
      text => countItemText(text) <= MATCH_ITEM_TEXT_MAX_LENGTH,
      'Match item text must be at most 50 characters',
    ),
    richText: optional(string()),
    equation: optional(string()),
    imageUrl: optional(string()),
  })
    .refine(hasQuizItemContent, {
      message: MATCH_ITEM_CONTENT_REQUIRED_MESSAGE,
    })
    .refine(
      item => !hasFormulaOverLength(item, ITEM_FORMULA_LATEX_MAX_LENGTH),
      {
        message: 'Match item formula must be at most 100 characters',
        path: ['text'],
      },
    );

const AnswerPairSchema = object({
  leftId: MatchItemIdSchema,
  rightId: MatchItemIdSchema,
});

const createQuizMatchDataSchema = (countItemText: (text: string) => number) =>
  object({
    leftItems: array(createMatchItemSchema(countItemText))
      .min(MIN_MATCH_PAIR_COUNT)
      .max(MAX_MATCH_PAIR_COUNT),
    rightItems: array(createMatchItemSchema(countItemText))
      .min(MIN_MATCH_PAIR_COUNT)
      .max(MAX_MATCH_PAIR_COUNT),
    answerPairs: array(AnswerPairSchema)
      .min(MIN_MATCH_PAIR_COUNT)
      .max(MAX_MATCH_PAIR_COUNT),
  }).refine(
    data => {
      if (
        data.leftItems.length !== data.rightItems.length ||
        data.leftItems.length !== data.answerPairs.length
      ) {
        return false;
      }

      const leftIds = new Set(data.leftItems.map(item => item.id));
      const rightIds = new Set(data.rightItems.map(item => item.id));
      const allItemIds = new Set([...leftIds, ...rightIds]);
      const pairedLeftIds = new Set(data.answerPairs.map(pair => pair.leftId));
      const pairedRightIds = new Set(
        data.answerPairs.map(pair => pair.rightId),
      );

      return (
        leftIds.size === data.leftItems.length &&
        rightIds.size === data.rightItems.length &&
        allItemIds.size === data.leftItems.length + data.rightItems.length &&
        pairedLeftIds.size === leftIds.size &&
        pairedRightIds.size === rightIds.size &&
        data.answerPairs.every(
          pair => leftIds.has(pair.leftId) && rightIds.has(pair.rightId),
        )
      );
    },
    {
      message: 'Every match item must be paired exactly once',
      path: ['answerPairs'],
    },
  );

/** 빌드 폼 검사. 보기 글자 수에서 수식을 보이는 글자 수로 센다(서버보다 엄격). */
export const QuizMatchDataSchema =
  createQuizMatchDataSchema(countVisibleQuizText);

/**
 * 서버·AI 가 보낸 짝맞추기 데이터를 받을 때 쓴다. 길이는 서버 규칙(수식 1자)으로 본다 — 서버가
 * 받아 주는 데이터를 받는 단계에서 버리지 않고, 새 기준은 폼 저장 검사(`QuizMatchDataSchema`)가 맡는다.
 */
export const QuizMatchDataServerSchema =
  createQuizMatchDataSchema(countQuizText);
